import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import BookCard from "@/components/BookCard";
import NewsletterForm from "@/components/NewsletterForm";
import { Section, SectionHeading } from "@/components/Section";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG =
  "https://images.unsplash.com/photo-1573912675615-a8099bda36db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxtb29keSUyMG5pZ2h0JTIwY2l0eSUyMGZvZ3xlbnwwfHx8fDE3ODIwOTYzMjd8MA&ixlib=rb-4.1.0&q=85";
const SECTION_TEXTURE =
  "https://images.pexels.com/photos/10901795/pexels-photo-10901795.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2400";

export default function Home() {
  const [books, setBooks] = useState([]);
  const [news, setNews] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 200]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.2]);

  useEffect(() => {
    axios.get(`${API}/books`).then((r) => setBooks(r.data)).catch(() => {});
    axios.get(`${API}/news`).then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const featured = books.find((b) => b.featured) || books[0];

  return (
    <div className="page-wrapper">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[100vh] flex items-center justify-center overflow-hidden"
      >
        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="absolute inset-0"
        >
          <img
            src={HERO_BG}
            alt="Moody midnight city skyline shrouded in mist"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Gradient overlay — preserves image while ensuring text contrast */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D24] via-[#1A1D24]/75 to-[#1A1D24]/45" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1A1D24]/40 via-transparent to-[#0F5B60]/15" />
          <div className="starfield" />
        </motion.div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center pt-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="overline mb-8"
          >
            ✦ Paranormal Romance · Urban Fantasy · Cozy Mystery ✦
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-[#F4F4F6] font-light leading-[1.02] tracking-tight"
          >
            Magic in the shadows.
            <br />
            Romance <span className="italic text-[#D4AF37]">in the dark.</span>
            <br />
            Mysteries waiting to unravel.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="text-[#A0A4AE] text-base md:text-lg max-w-xl mx-auto mt-8 leading-relaxed"
          >
            Step into worlds of paranormal romance, urban fantasy, and cozy supernatural mysteries — where every shadow holds a secret and every heart finds its match.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-12"
          >
            <Link to="/books" data-testid="hero-cta-books" className="btn-primary">
              Explore the Library <ArrowRight size={14} />
            </Link>
            <Link to="/about" data-testid="hero-cta-about" className="btn-ghost">
              Meet the Author
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
        >
          <span className="overline">Scroll</span>
          <div className="gilded-rule" />
        </motion.div>
      </section>

      {/* FEATURED BOOK */}
      {featured && (
        <Section testId="featured-section">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="md:col-span-5 relative"
            >
              <div className="relative aspect-[3/4] glass-card overflow-hidden">
                <img
                  src={featured.cover_image}
                  alt={featured.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>
              <div className="absolute -top-4 -right-4 w-32 h-32 border border-[#D4AF37]/30 rounded-full hidden md:block" />
              <div className="absolute -bottom-6 -left-6 w-20 h-20 border border-[#D4AF37]/20 rounded-full hidden md:block" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="md:col-span-7"
            >
              <div className="overline mb-5">✦ Now Available</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F4F6] font-light leading-[1.05] mb-6">
                {featured.title}
              </h2>
              <p className="font-serif italic text-2xl text-[#D4AF37] mb-8 leading-relaxed">
                "{featured.tagline}"
              </p>
              <p className="text-[#A0A4AE] text-base md:text-lg leading-relaxed mb-10 max-w-xl">
                {featured.description}
              </p>
              <div className="flex flex-wrap items-center gap-6 mb-10">
                <div>
                  <div className="overline mb-1">Genre</div>
                  <div className="text-[#F4F4F6] text-sm">{featured.genre}</div>
                </div>
                <div className="w-px h-10 bg-[#2B3A4A]/10" />
                <div>
                  <div className="overline mb-1">Pages</div>
                  <div className="text-[#F4F4F6] text-sm">{featured.pages || "—"}</div>
                </div>
                <div className="w-px h-10 bg-[#2B3A4A]/10" />
                <div>
                  <div className="overline mb-1">Released</div>
                  <div className="text-[#F4F4F6] text-sm">{featured.release_date}</div>
                </div>
              </div>
              <Link to={`/books/${featured.slug}`} data-testid="featured-cta" className="btn-primary">
                Read the Story <ArrowRight size={14} />
              </Link>
            </motion.div>
          </div>
        </Section>
      )}

      {/* THE LIBRARY */}
      <Section testId="library-section">
        <SectionHeading
          overline="The Library"
          title="Spells that bind. Loves that endure."
          kicker="A growing collection of paranormal romances, urban fantasies, and cozy supernatural mysteries."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {books.slice(0, 3).map((b, i) => (
            <BookCard key={b.id} book={b} index={i} />
          ))}
        </div>
        <div className="mt-16 text-center">
          <Link to="/books" data-testid="library-view-all" className="btn-primary">
            View Every Book <ArrowRight size={14} />
          </Link>
        </div>
      </Section>

      {/* NEWS */}
      {news.length > 0 && (
        <Section testId="home-news-section" className="bg-[#2B3A4A]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="overline mb-5">Dispatches</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F4F6] font-light leading-[1.05] max-w-2xl">
              Whispers from the writing room.
            </h2>
          </div>
          <Link to="/news" data-testid="news-view-all" className="btn-ghost text-sm self-start md:self-end">
            All News →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {news.map((post, i) => (
            <motion.article
              key={post.id}
              data-testid={`news-card-${post.slug}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="glass-card overflow-hidden group"
            >
              <Link to={`/news/${post.slug}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  />
                </div>
                <div className="p-7">
                  <div className="overline mb-3">{post.category} · {post.date}</div>
                  <h3 className="font-serif text-2xl text-[#F4F4F6] leading-tight mb-3 group-hover:text-[#D4AF37] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-[#A0A4AE] text-sm leading-relaxed line-clamp-2">
                    {post.excerpt}
                  </p>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </Section>
      )}

      {/* NEWSLETTER */}
      <section
        id="newsletter"
        data-testid="newsletter-section"
        className="relative py-32 md:py-40 overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(${SECTION_TEXTURE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1D24] via-[#1A1D24]/85 to-[#1A1D24]" />
        <div className="relative max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="overline mb-5">The Letter</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F4F6] font-light leading-[1.05]">
              Sign on for <span className="italic text-[#D4AF37]">starlit</span> dispatches.
            </h2>
            <p className="text-[#A0A4AE] mt-6 text-base md:text-lg max-w-xl mx-auto">
              First chapters, cover reveals, and the occasional confession. Quiet, monthly, never spam.
            </p>
          </div>
          <NewsletterForm />
        </div>
      </section>
    </div>
  );
}
